import { useEffect, useMemo, useRef, useState } from "react";
import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import type { NotificationStreamItem, RuntimeStreamEvent } from "@situate/contracts";
import { runtimeStreamEventSchema } from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const NOTIFICATIONS_STREAM_PATH = "/v1/live/streams/notifications";
const HEARTBEAT_TIMEOUT_MS = 60_000;
const MAX_RETRY_DELAY_MS = 30_000;
const BASE_RETRY_DELAY_MS = 1_000;

export type NotificationsStreamStatus =
  "disabled" | "connecting" | "open" | "retrying" | "offline" | "auth-failed" | "error";

interface DeploymentNotificationsState {
  deploymentId: string | null;
  /** True once the first `notification_snapshot` for the current connection has been applied. */
  initialized: boolean;
  notifications: Map<string, NotificationStreamItem>;
  unseenIds: Set<string>;
  /** Increments on every applied `notification_added`; paired with `lastAddedItem` so a
   * consumer's effect can detect and react to each individual addition exactly once. */
  addedSeq: number;
  lastAddedItem: NotificationStreamItem | null;
  /** Increments on every applied `notification_removed`; paired with `lastRemovedId`. */
  removedSeq: number;
  lastRemovedId: string | null;
}

function createEmptyState(deploymentId: string | null): DeploymentNotificationsState {
  return {
    deploymentId,
    initialized: false,
    notifications: new Map(),
    unseenIds: new Set(),
    addedSeq: 0,
    lastAddedItem: null,
    removedSeq: 0,
    lastRemovedId: null,
  };
}

export interface UseNotificationsStreamResult {
  /** Streamed notifications, newest `created_at` first
   * (`notification_id` as a stable tie-breaker). */
  notifications: readonly NotificationStreamItem[];
  /** Notification IDs added since the frontend last viewed the bell panel. */
  unseenIds: ReadonlySet<string>;
  status: NotificationsStreamStatus;
  /** Increments on every applied `notification_added`; paired with `lastAddedItem`. */
  addedSeq: number;
  lastAddedItem: NotificationStreamItem | null;
  /** Increments on every applied `notification_removed`; paired with `lastRemovedId`. */
  removedSeq: number;
  lastRemovedId: string | null;
  /** Clears the given IDs from the unseen set, e.g. when the bell panel is viewed. */
  markSeen: (ids: Iterable<string>) => void;
}

function sortNotifications(
  notifications: Iterable<NotificationStreamItem>
): NotificationStreamItem[] {
  return Array.from(notifications).sort((a, b) => {
    const rankDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return rankDiff !== 0 ? rankDiff : a.notification_id.localeCompare(b.notification_id);
  });
}

/**
 * Subscribes to the runtime API's deployment-wide notification SSE stream
 * (`GET /v1/live/streams/notifications`) for the current deployment's runtime
 * session, and keeps the authorized notification set plus
 * a frontend-only unseen-ID set in state.
 *
 * Mirrors `useLiveTracksStream`'s connection lifecycle (visibility/online
 * gating, heartbeat watchdog, generation-guarded callbacks, exponential
 * backoff, and 401/403 handling), but the subscription is always
 * deployment-wide: it never accepts or reacts to an area filter.
 *
 * The first snapshot on each connection (including after a reconnect) replaces
 * state wholesale. Additions received before that snapshot are buffered and
 * merged into it so a notification cannot be lost at the snapshot boundary.
 * Callers (e.g. for a popup) should treat `addedSeq` changes as "show a popup
 * for `lastAddedItem`". Callers wanting to mark notifications as viewed (e.g.
 * opening the bell panel) call `markSeen`.
 */
export function useNotificationsStream(
  deploymentId: string,
  enabled = true
): UseNotificationsStreamResult {
  const runtime = useRuntimeHttpClient();
  const [state, setState] = useState<DeploymentNotificationsState>(() =>
    createEmptyState(deploymentId)
  );
  const [status, setStatus] = useState<NotificationsStreamStatus>("disabled");
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const lastEventAtRef = useRef<number>(Date.now());
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryAttemptRef = useRef(0);

  function getRetryDelay(attempt: number): number {
    const exponentialDelay = Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** attempt);
    const jitter = 0.8 + Math.random() * 0.4;
    return exponentialDelay * jitter;
  }

  function clearTimers() {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (watchdogIntervalRef.current) {
      clearInterval(watchdogIntervalRef.current);
      watchdogIntervalRef.current = null;
    }
  }

  function resetRetryState() {
    retryAttemptRef.current = 0;
  }

  // Reset all notification state (including unseen IDs, popup sequencing, and
  // the initialized flag) whenever the deployment changes.
  useEffect(() => {
    setState((previous) =>
      previous.deploymentId !== deploymentId ? createEmptyState(deploymentId) : previous
    );
  }, [deploymentId]);

  useEffect(() => {
    function handleVisibilityChange() {
      setIsVisible(document.visibilityState === "visible");
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const hasNotificationsGrant = runtime.operationGrants.some(
      (grant) => grant.operation_id === "live_notifications_stream"
    );

    const shouldStream =
      enabled &&
      isVisible &&
      isOnline &&
      runtime.client !== null &&
      runtime.deploymentId === deploymentId &&
      hasNotificationsGrant;

    if (!shouldStream) {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      clearTimers();

      if (!enabled) {
        setStatus("disabled");
      } else if (!isOnline) {
        setStatus("offline");
      } else if (!isVisible || !runtime.client || !hasNotificationsGrant) {
        setStatus("disabled");
      }

      return;
    }

    const { client } = runtime;
    if (!client) {
      return;
    }

    function startStream() {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }

      const generation = ++generationRef.current;
      const controller = new AbortController();
      controllerRef.current = controller;
      let hasReceivedSnapshot = false;
      const pendingAddedItems = new Map<string, NotificationStreamItem>();

      lastEventAtRef.current = Date.now();
      setStatus("connecting");

      function isStaleCallback(): boolean {
        return generation !== generationRef.current || controller.signal.aborted;
      }

      function applyEvent(event: RuntimeStreamEvent) {
        setState((previous) => {
          if (previous.deploymentId !== deploymentId) {
            return previous;
          }

          switch (event.event_type) {
            case "notification_snapshot": {
              hasReceivedSnapshot = true;
              const notifications = new Map(
                event.data.notifications.map((item) => [item.notification_id, item] as const)
              );
              const unseenIds = new Set<string>();
              let addedSeq = previous.addedSeq;
              let lastAddedItem: NotificationStreamItem | null = null;
              for (const item of pendingAddedItems.values()) {
                if (notifications.has(item.notification_id)) {
                  continue;
                }
                notifications.set(item.notification_id, item);
                unseenIds.add(item.notification_id);
                addedSeq += 1;
                lastAddedItem = item;
              }
              pendingAddedItems.clear();
              return {
                deploymentId,
                initialized: true,
                notifications,
                unseenIds,
                addedSeq,
                lastAddedItem,
                removedSeq: previous.removedSeq,
                lastRemovedId: null,
              };
            }
            case "notification_added": {
              if (!hasReceivedSnapshot) {
                pendingAddedItems.set(event.data.notification_id, event.data);
                return previous;
              }
              const notifications = new Map(previous.notifications);
              notifications.set(event.data.notification_id, event.data);
              const unseenIds = new Set(previous.unseenIds);
              unseenIds.add(event.data.notification_id);
              return {
                ...previous,
                notifications,
                unseenIds,
                addedSeq: previous.addedSeq + 1,
                lastAddedItem: event.data,
              };
            }
            case "notification_updated": {
              if (!previous.notifications.has(event.data.notification_id)) {
                return previous;
              }
              const notifications = new Map(previous.notifications);
              notifications.set(event.data.notification_id, event.data);
              return { ...previous, notifications };
            }
            case "notification_removed": {
              const notificationId = event.data.notification_id;
              if (!previous.notifications.has(notificationId)) {
                return previous;
              }
              const notifications = new Map(previous.notifications);
              notifications.delete(notificationId);
              const unseenIds = new Set(previous.unseenIds);
              unseenIds.delete(notificationId);
              return {
                ...previous,
                notifications,
                unseenIds,
                removedSeq: previous.removedSeq + 1,
                lastRemovedId: notificationId,
              };
            }
            default:
              return previous;
          }
        });
      }

      function onMessage(message: EventSourceMessage) {
        if (isStaleCallback()) {
          return;
        }

        lastEventAtRef.current = Date.now();

        if (message.event === "connection_established" || message.event === "heartbeat") {
          setStatus("open");
          resetRetryState();
          return;
        }

        if (!message.data) {
          return;
        }

        let parsedEvent: RuntimeStreamEvent;
        try {
          parsedEvent = runtimeStreamEventSchema.parse(JSON.parse(message.data));
        } catch {
          console.error("[notifications-stream] failed to parse an incoming message");
          return;
        }

        setStatus("open");
        resetRetryState();
        applyEvent(parsedEvent);
      }

      function onError(error: unknown) {
        if (isStaleCallback()) {
          return;
        }

        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        let shouldRetry = true;
        const retryDelay = getRetryDelay(retryAttemptRef.current);

        if (error instanceof Error) {
          const message = error.message.toLowerCase();

          if (message.includes("403") || message.includes("forbidden")) {
            console.error("[notifications-stream] access denied (403), not retrying");
            setStatus("auth-failed");
            shouldRetry = false;
          } else if (message.includes("401") || message.includes("unauthorized")) {
            shouldRetry = false;

            if (runtime.refreshSession) {
              console.warn("[notifications-stream] unauthorized (401), refreshing runtime session");
              setStatus("retrying");
              void runtime.refreshSession().catch((refreshError: unknown) => {
                if (generation === generationRef.current) {
                  console.error(
                    "[notifications-stream] failed to refresh runtime session",
                    refreshError
                  );
                  setStatus("error");
                }
              });
            } else {
              console.error("[notifications-stream] unauthorized (401), refresh unavailable");
              setStatus("auth-failed");
            }
          } else {
            console.warn(
              `[notifications-stream] transient error, will retry in ${Math.round(retryDelay)}ms:`,
              error
            );
            setStatus("retrying");
            retryAttemptRef.current++;
          }
        }

        if (shouldRetry) {
          retryTimeoutRef.current = setTimeout(() => {
            if (generation === generationRef.current) {
              startStream();
            }
          }, retryDelay);
        }
      }

      if (!client) {
        console.error("[notifications-stream] client became null during startStream");
        return;
      }

      client.subscribe(NOTIFICATIONS_STREAM_PATH, { onMessage }, controller.signal).catch(onError);
    }

    startStream();

    watchdogIntervalRef.current = setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastEventAtRef.current;

      if (timeSinceLastEvent > HEARTBEAT_TIMEOUT_MS) {
        console.warn(
          `[notifications-stream] no events for ${Math.round(timeSinceLastEvent / 1000)}s, restarting stream`
        );

        if (controllerRef.current) {
          controllerRef.current.abort();
        }
        startStream();
      }
    }, HEARTBEAT_TIMEOUT_MS / 2);

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      clearTimers();
    };
  }, [deploymentId, runtime, enabled, isVisible, isOnline]);

  const hasCurrentScope = state.deploymentId === deploymentId;

  const orderedNotifications = useMemo(
    () => (hasCurrentScope ? sortNotifications(state.notifications.values()) : []),
    [hasCurrentScope, state.notifications]
  );

  function markSeen(ids: Iterable<string>) {
    const idsToClear = new Set(ids);
    if (idsToClear.size === 0) {
      return;
    }
    setState((previous) => {
      if (previous.deploymentId !== deploymentId) {
        return previous;
      }
      let changed = false;
      const unseenIds = new Set(previous.unseenIds);
      for (const id of idsToClear) {
        if (unseenIds.delete(id)) {
          changed = true;
        }
      }
      return changed ? { ...previous, unseenIds } : previous;
    });
  }

  return {
    notifications: orderedNotifications,
    unseenIds: hasCurrentScope ? state.unseenIds : new Set(),
    status,
    addedSeq: hasCurrentScope ? state.addedSeq : 0,
    lastAddedItem: hasCurrentScope ? state.lastAddedItem : null,
    removedSeq: hasCurrentScope ? state.removedSeq : 0,
    lastRemovedId: hasCurrentScope ? state.lastRemovedId : null,
    markSeen,
  };
}
